/**
 * The trial banner (days left, or read-only once it ends) and the six-step
 * getting-started checklist that ticks itself off from what the agency has
 * really done. The checklist can be hidden once the agency no longer needs it.
 */

import { useEffect, useState, type ReactNode } from 'react';

import { apiCall, orgPath, type Organisation } from '../api.ts';
import type { Client } from '../reports/reports-api.ts';

const MS_PER_DAY = 86_400_000;
const HIDE_KEY = 'synanalytics_hide_getting_started';

export function TrialBanner({ organisation }: { organisation: Organisation }): ReactNode {
  if (organisation.plan !== 'TRIAL' || !organisation.trial_ends_at) return null;
  const left = Math.ceil((Date.parse(organisation.trial_ends_at) - Date.now()) / MS_PER_DAY);
  return (
    <div className={`ag-trial${left <= 0 ? ' ag-trial-ended' : ''}`} role="status">
      {left > 0
        ? `🚀 ${left} ${left === 1 ? 'day' : 'days'} left in your free trial.`
        : 'Your free trial has ended. Your workspace is read-only until you choose a plan.'}
    </div>
  );
}

interface Step {
  key: string;
  label: string;
  done: boolean;
}

function hrefFor(step: string, clients: Client[]): string | null {
  const first = clients[0]?.id;
  const client = (tab: string) => (first ? `#/client/${first}/${tab}` : null);
  switch (step) {
    case 'source':
      return client('data');
    case 'dashboard':
      return client('dashboards');
    case 'report':
      return client('reports');
    case 'invite':
      return client('users');
    case 'branding':
      return '#/settings';
    default:
      return null;
  }
}

export function GettingStarted({
  organisationId,
  clients,
  onAddClient,
}: {
  organisationId: string;
  clients: Client[];
  onAddClient: () => void;
}): ReactNode {
  const [steps, setSteps] = useState<Step[] | null>(null);
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(HIDE_KEY) === organisationId;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    apiCall<{ steps: Step[] }>(orgPath(organisationId, '/reports/getting-started'))
      .then((data) => setSteps(data.steps))
      .catch(() => setSteps(null));
  }, [organisationId, clients.length]);

  if (hidden || !steps) return null;
  const done = steps.filter((step) => step.done).length;
  const hide = () => {
    setHidden(true);
    try {
      localStorage.setItem(HIDE_KEY, organisationId);
    } catch {
      // Hiding is a convenience; it simply comes back next time.
    }
  };
  return (
    <section className="ag-start" aria-labelledby="ag-start-title">
      <header className="ag-start-head">
        <h2 id="ag-start-title">Getting started</h2>
        <span className="ag-start-count">
          {done} of {steps.length} done
        </span>
        <span className="ag-spacer" />
        <button type="button" className="rh-text-btn ag-start-hide" onClick={hide}>
          Hide
        </button>
      </header>
      <div
        className="ag-start-bar"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={steps.length}
        aria-valuenow={done}
        aria-label="Getting started progress"
      >
        <span style={{ width: `${(done / steps.length) * 100}%` }} />
      </div>
      <ol className="ag-start-list">
        {steps.map((step) => {
          const href = hrefFor(step.key, clients);
          const label = step.done ? <s>{step.label}</s> : step.label;
          return (
            <li key={step.key} className={step.done ? 'done' : ''}>
              <span className="ag-start-tick" aria-hidden="true">
                {step.done ? '✓' : ''}
              </span>
              {step.done && label}
              {!step.done && step.key === 'client' && (
                <button type="button" className="ag-start-link" onClick={onAddClient}>
                  {label}
                </button>
              )}
              {!step.done &&
                step.key !== 'client' &&
                (href ? (
                  <a className="ag-start-link" href={href}>
                    {label}
                  </a>
                ) : (
                  <span>
                    {label} <small>(add a client first)</small>
                  </span>
                ))}
            </li>
          );
        })}
      </ol>
    </section>
  );
}
